import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  type ResourceLinkAttributes,
  createResourceLinkExtension,
  extractLastNameComponent,
  isResourceName,
} from '@spotify/confidence-core-react';
import { FeatureFlagged } from '@spotify/confidence-flag-evaluation-react';
import { metricRouteRef } from '@spotify/plugin-confidence-metrics-react';

import { metricsPlugin } from '../plugin';
import { measurementRouteRef } from '../routes';

interface MetricResourceLinkProps extends ResourceLinkAttributes {
  name: string;
  text: ReactNode;
  className?: string;
}

const MetricResourceLink = ({
  name,
  text,
  className,
  target,
  rel,
}: MetricResourceLinkProps) => {
  const routeRef = useRouteRef(metricRouteRef);
  const metricId = extractLastNameComponent(name);

  if (!routeRef || !metricId) {
    return <>{text}</>;
  }

  const to = routeRef({ id: metricId });

  return (
    <Link to={to} className={className} target={target} rel={rel}>
      {text}
    </Link>
  );
};

export const MetricResourceLinkExtension = metricsPlugin.provide(
  createResourceLinkExtension({
    id: 'metric-resource-link',
    predicate: ({ name }) => isResourceName(name, 'metrics'),
    icon: 'metric',
    render: ({ name, text, className, target, rel }) => (
      <MetricResourceLink
        name={name}
        text={text}
        className={className}
        target={target}
        rel={rel}
      />
    ),
  }),
);

interface MeasurementResourceLinkProps extends ResourceLinkAttributes {
  name: string;
  text: ReactNode;
  className?: string;
}

const MeasurementResourceLink = ({
  name,
  text,
  className,
  target,
  rel,
}: MeasurementResourceLinkProps) => {
  const routeRef = useRouteRef(measurementRouteRef);
  const measurementId = extractLastNameComponent(name);

  if (!routeRef || !measurementId) {
    return <>{text}</>;
  }

  const to = routeRef({ id: measurementId });

  return (
    <FeatureFlagged name="measurements">
      <FeatureFlagged.On>
        <Link to={to} className={className} target={target} rel={rel}>
          {text}
        </Link>
      </FeatureFlagged.On>
      <FeatureFlagged.Off>{text}</FeatureFlagged.Off>
    </FeatureFlagged>
  );
};

export const MeasurementResourceLinkExtension = metricsPlugin.provide(
  createResourceLinkExtension({
    id: 'measurement-resource-link',
    predicate: ({ name }) => isResourceName(name, 'measurements'),
    icon: 'measurement',
    render: ({ name, text, className, target, rel }) => (
      <MeasurementResourceLink
        name={name}
        text={text}
        className={className}
        target={target}
        rel={rel}
      />
    ),
  }),
);
